import i18n from "@/i18n.ts";
import { isJobFieldValueEmpty } from "@/pages/recruit/jobs/edit/job-edit-fields.ts";
import { ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import {
  ApiCompensationTypeEnum,
  ApiOfferConfig,
  ApiOfferSalarySave,
  ApiOfferSavePayload,
  OfferEditFormData,
  OfferEditSystemFormData,
  OfferFormSection,
  offerConfigSchema,
  offerSalarySchema,
} from "@/types/recruit/offer.types.ts";
import { z } from "zod";

type OfferSystemFieldName = Extract<
  keyof OfferEditSystemFormData,
  | "first_name"
  | "last_name"
  | "job_title"
  | "date_start"
  | "legal_entity_id"
  | "location_id"
  | "manager_id"
  | "salary"
  | "offer_text"
>;

type OfferHideConfigKey = Extract<
  keyof ApiOfferConfig,
  "hide_location" | "hide_manager" | "hide_legal_entity" | "hide_salary"
>;

export const offerFieldNames: Partial<Record<ApiFieldTypes, OfferSystemFieldName>> = {
  [ApiFieldTypes.offer_first_name]: "first_name",
  [ApiFieldTypes.offer_last_name]: "last_name",
  [ApiFieldTypes.offer_job_title]: "job_title",
  [ApiFieldTypes.offer_date_start]: "date_start",
  [ApiFieldTypes.offer_legal_entity]: "legal_entity_id",
  [ApiFieldTypes.offer_location]: "location_id",
  [ApiFieldTypes.offer_manager]: "manager_id",
  [ApiFieldTypes.offer_salary]: "salary",
  [ApiFieldTypes.offer_text]: "offer_text",
};

export function getOfferFormSections(sections: OfferFormSection[]) {
  return sections
    .map((section) => ({
      ...section,
      fields: section.fields.filter(
        (field) => field.type === ApiFieldTypes.custom || field.type in offerFieldNames
      ),
    }))
    .filter((section) => section.fields.length > 0);
}

export function getOfferFieldNames(fields: ApiField[]) {
  return new Set(fields.flatMap((field) => offerFieldNames[field.type] ?? []));
}

export function getVisibleOfferValues<T extends OfferEditSystemFormData>(
  values: T,
  fields: ApiField[]
): T {
  const names = getOfferFieldNames(fields);
  const hiddenValues = Object.values(offerFieldNames)
    .filter((name) => name !== "first_name" && name !== "last_name" && !names.has(name))
    .map((name) => [name, name === "offer_text" ? "" : null]);
  return { ...values, ...Object.fromEntries(hiddenValues) };
}

export const offerHideConfigKeys: Partial<Record<ApiFieldTypes, OfferHideConfigKey>> = {
  [ApiFieldTypes.offer_location]: "hide_location",
  [ApiFieldTypes.offer_manager]: "hide_manager",
  [ApiFieldTypes.offer_legal_entity]: "hide_legal_entity",
  [ApiFieldTypes.offer_salary]: "hide_salary",
};

const offerPreviewSystemFieldTypes = new Set([
  ApiFieldTypes.offer_date_start,
  ApiFieldTypes.offer_legal_entity,
  ApiFieldTypes.offer_location,
  ApiFieldTypes.offer_manager,
  ApiFieldTypes.offer_salary,
]);

export function getOfferPreviewFields(fields: ApiField[], values: OfferEditFormData) {
  return fields.filter((field) => {
    if (field.type === ApiFieldTypes.custom) return !isJobFieldValueEmpty(values[field.slug]);
    if (!offerPreviewSystemFieldTypes.has(field.type)) return false;
    if (field.type === ApiFieldTypes.offer_salary) return !!values.salary?.salary_value;
    const name = offerFieldNames[field.type];
    return !!name && !isJobFieldValueEmpty(values[name]);
  });
}

export function getOfferServerFieldMap(fields: ApiField[]): Record<string, string> {
  return Object.fromEntries(
    fields.flatMap((field) => {
      if (field.type === ApiFieldTypes.custom) return [[`custom_fields.${field.slug}`, field.slug]];
      const name = offerFieldNames[field.type];
      if (!name) return [];
      return [[field.slug, name === "salary" ? "salary.salary_type" : name]];
    })
  );
}

export function buildOfferSchema(fields: ApiField[], customFieldsShape: Record<string, z.ZodType>) {
  const requiredMessage = i18n.t("errors:field_required");
  const mandatoryNames = getOfferFieldNames(fields.filter((field) => field.is_mandatory));
  const mandatory = <T extends z.ZodType>(name: OfferSystemFieldName, schema: T) =>
    mandatoryNames.has(name)
      ? schema.refine((value) => !isJobFieldValueEmpty(value), requiredMessage)
      : schema;

  return z
    .object({
      first_name: z.string({ error: requiredMessage }).trim().min(1, requiredMessage),
      last_name: z.string({ error: requiredMessage }).trim().min(1, requiredMessage),
      job_title: mandatory("job_title", z.string().nullable()),
      date_start: mandatory("date_start", z.date().nullable()),
      legal_entity_id: mandatory("legal_entity_id", z.number().nullable()),
      location_id: mandatory("location_id", z.number().nullable()),
      manager_id: mandatory("manager_id", z.number().nullable()),
      salary: mandatoryNames.has("salary")
        ? offerSalarySchema.refine((value) => value !== null, {
            message: requiredMessage,
            path: ["salary_type"],
          })
        : offerSalarySchema,
      config: offerConfigSchema,
      offer_template_id: z.number().nullable(),
      offer_text: mandatory("offer_text", z.string()),
    })
    .and(z.object(customFieldsShape));
}

export function toOfferSalaryPayload(salary: ApiOfferSalarySave | null) {
  return salary && Object.values(salary).some(Boolean)
    ? {
        salary_type: salary.salary_type || null,
        salary_value:
          salary.salary_value && salary.salary_type !== ApiCompensationTypeEnum.custom
            ? salary.salary_value.replace(",", ".")
            : salary.salary_value || null,
        currency_code: salary.currency_code || null,
      }
    : null;
}

export function getOfferSystemPayload(
  values: OfferEditSystemFormData,
  fields: ApiField[],
  isCreate: boolean
): ApiOfferSavePayload {
  const names = getOfferFieldNames(fields);
  const value = <T>(name: OfferSystemFieldName, fieldValue: T) =>
    names.has(name) ? fieldValue : isCreate ? null : undefined;

  return {
    first_name: values.first_name,
    last_name: values.last_name,
    job_title: value("job_title", values.job_title || null),
    date_start: value("date_start", values.date_start || null),
    legal_entity_id: value("legal_entity_id", values.legal_entity_id || null),
    location_id: value("location_id", values.location_id || null),
    manager_id: value("manager_id", values.manager_id || null),
    salary: value("salary", toOfferSalaryPayload(values.salary)),
    offer_text: value("offer_text", values.offer_text || ""),
  };
}
